import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import {
  Mail,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Instagram,
} from "lucide-react";

const FacebookBrandIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 320 512" className={className} aria-hidden="true" fill="currentColor">
    <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-50 py-12 border-t border-slate-100" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative flex flex-col items-center gap-3">
          <Link to="/" className="text-slate-900 font-semibold text-center">
            Web & Studio
          </Link>
          <p className="text-sm text-slate-500 text-center">© {currentYear} Web & Studio</p>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-slate-700 transition-colors">
              Privacidade
            </Link>
            <Link to="/cookies" className="hover:text-slate-700 transition-colors">
              Cookies
            </Link>
            <Link to="/terms" className="hover:text-slate-700 transition-colors">
              Termos
            </Link>
          </div>
          <div className="absolute right-0 top-0 flex items-center gap-3">
            <a
              href="https://www.instagram.com/webstudio026/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-black hover:text-black/70 transition-colors"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61587668724425"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="text-black hover:text-black/70 transition-colors"
            >
              <FacebookBrandIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function Thanks() {
  const location = useLocation();
  const queryName = new URLSearchParams(location.search).get("name") || "";
  const userName = location?.state?.name || queryName;
  const submissionFailed = Boolean(location?.state?.submissionFailed);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Seo
        title="Obrigado | Web & Studio"
        description="Confirmação de envio do formulário de contacto da Web & Studio."
        path="/thanks"
        robots="noindex, nofollow"
      />
      <nav className="fixed top-0 left-0 right-0 z-50 navbar-glass py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="thankyou-logo">
            <img
              src="/Logo.png"
              alt="Web & Studio Logo"
              className="h-[80px] md:h-[88px] lg:h-[96px] w-auto object-contain"
            />
          </Link>
          <Link to="/">
            <Button
              variant="outline"
              className="mt-1 rounded-full px-6 border-slate-300 hover:bg-slate-100"
              data-testid="thankyou-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao Início
            </Button>
          </Link>
        </div>
      </nav>

      <main className="pt-40 pb-24" data-testid="thankyou-page">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center space-y-8">
            <div
              className={`inline-flex items-center justify-center w-24 h-24 rounded-full animate-fade-in-up ${
                submissionFailed ? "bg-red-100" : "bg-green-100"
              }`}
              style={{ animationDelay: "0.1s", opacity: 0, animationFillMode: "forwards" }}
            >
              {submissionFailed ? (
                <XCircle className="w-12 h-12 text-red-700" strokeWidth={1.5} />
              ) : (
                <CheckCircle2 className="w-12 h-12 text-green-700" strokeWidth={1.5} />
              )}
            </div>

            <div
              className="space-y-4 animate-fade-in-up"
              style={{ animationDelay: "0.2s", opacity: 0, animationFillMode: "forwards" }}
            >
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900">
                {submissionFailed ? "Desculpe, ocorreu um erro no envio" : `Obrigado${userName ? `, ${userName}` : ""}!`}
              </h1>
              <p className="text-xl md:text-2xl text-slate-600 max-w-2xl mx-auto">
                {submissionFailed
                  ? "A mensagem não foi enviada. Por favor, envie email para o contacto no fim do site."
                  : "A sua mensagem foi enviada com sucesso."}
              </p>
            </div>

            <div
              className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-100 max-w-2xl mx-auto animate-fade-in-up"
              style={{ animationDelay: "0.3s", opacity: 0, animationFillMode: "forwards" }}
            >
              <div className="space-y-6">
                <div className="w-16 h-16 bg-teal-50 rounded-xl flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-teal-700" />
                </div>

                <div className="space-y-4">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                    {submissionFailed ? "O que fazer agora?" : "O que acontece agora?"}
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {submissionFailed
                      ? "Como o envio falhou, pedimos que nos contactes por email para garantir que recebemos o teu pedido."
                      : "A nossa equipa irá analisar a sua mensagem e entraremos em contacto consigo o mais brevemente possível, normalmente dentro de 24 a 48 horas úteis."}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <p className="text-slate-500 text-sm">
                    {submissionFailed
                      ? "Encontra o email de contacto no fim do site."
                      : "Enquanto isso, pode continuar a explorar os nossos serviços."}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 animate-fade-in-up"
              style={{ animationDelay: "0.4s", opacity: 0, animationFillMode: "forwards" }}
            >
              <Link to="/">
                <Button
                  className="btn-primary bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 py-6 text-base font-medium shadow-lg shadow-teal-700/20"
                  data-testid="thankyou-home-btn"
                >
                  Voltar à Página Inicial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div
              className="pt-8 animate-fade-in-up"
              style={{ animationDelay: "0.5s", opacity: 0, animationFillMode: "forwards" }}
            >
              <p className="text-slate-500 flex items-center justify-center gap-2">
                Feito pela Web & Studio
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

